import logger from '@utils/logger'
/**
 * Configuration Context
 * Provides centralized configuration to all components
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import {
  DISABLED_RELEASE_CAMPAIGN,
  getReleaseCampaignRefreshDelay,
} from '@utils/releaseCampaign'
import { isEvaluatedFeatureEnabled } from '@utils/featureFlags'
import configService from '../services/configService'

const ConfigContext = createContext(null)

export const useConfig = () => {
  const context = useContext(ConfigContext)
  if (!context) {
    throw new Error('useConfig must be used within ConfigProvider')
  }
  return context
}

export const ConfigProvider = ({ children }) => {
  const [paperTradingConfig, setPaperTradingConfig] = useState(null)
  const [evaluatedFeatures, setEvaluatedFeatures] = useState({
    env: 'unknown',
    features: {},
  })
  const [releaseCampaign, setReleaseCampaign] = useState(
    DISABLED_RELEASE_CAMPAIGN,
  )
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    loadConfiguration()
  }, [])

  useEffect(() => {
    let active = true

    const refreshRuntimeConfig = async () => {
      const [featuresResult, campaignResult] = await Promise.allSettled([
        configService.getEvaluatedFeatures('dashboard', { force: true }),
        configService.getReleaseCampaign({ force: true }),
      ])

      if (!active) {
        return
      }

      if (featuresResult.status === 'fulfilled') {
        setEvaluatedFeatures(featuresResult.value)
      } else {
        logger.error(
          '[ConfigContext] Failed to refresh evaluated features:',
          featuresResult.reason,
        )
      }

      if (campaignResult.status === 'fulfilled') {
        setReleaseCampaign(campaignResult.value)
      } else {
        logger.error(
          '[ConfigContext] Failed to refresh release campaign:',
          campaignResult.reason,
        )
      }
    }

    const refreshVisibleRuntimeConfig = () => {
      if (document.visibilityState === 'visible') {
        refreshRuntimeConfig()
      }
    }
    const refreshDelay = getReleaseCampaignRefreshDelay()
    const intervalId = window.setInterval(
      refreshVisibleRuntimeConfig,
      refreshDelay,
    )
    window.addEventListener('focus', refreshRuntimeConfig)

    return () => {
      active = false
      window.clearInterval(intervalId)
      window.removeEventListener('focus', refreshRuntimeConfig)
    }
  }, [])

  const loadConfiguration = async () => {
    try {
      setLoading(true)
      setError(null)

      // Load configurations in parallel
      const [paperTrading, evaluated, campaign] = await Promise.all([
        configService.getPaperTradingConfig(),
        configService.getEvaluatedFeatures('dashboard'),
        configService.getReleaseCampaign(),
      ])

      setPaperTradingConfig(paperTrading)
      setEvaluatedFeatures(evaluated)
      setReleaseCampaign(campaign)
      setLoading(false)
    } catch (err) {
      logger.error('[ConfigContext] Failed to load configuration:', err)
      setError(err.message)
      setLoading(false)

      // Use default fallback config
      setPaperTradingConfig(configService.getDefaultPaperTradingConfig())
      setEvaluatedFeatures({ env: 'unknown', features: {} })
      setReleaseCampaign(DISABLED_RELEASE_CAMPAIGN)
    }
  }

  const refreshConfig = () => {
    configService.clearCache()
    loadConfiguration()
  }

  /**
   * Check if a feature is enabled for the current environment
   * @param {string} featurePath - Feature path (e.g., 'dashboard.apiPlayground')
   * @returns {boolean} Whether the feature is enabled
   */
  const isFeatureEnabled = useCallback(
    (featurePath) => {
      return isEvaluatedFeatureEnabled(evaluatedFeatures, featurePath)
    },
    [evaluatedFeatures],
  )

  /**
   * Get current environment
   * @returns {string} Current environment
   */
  const getEnvironment = useCallback(() => {
    return evaluatedFeatures.env
  }, [evaluatedFeatures])

  const value = {
    paperTradingConfig,
    evaluatedFeatures,
    releaseCampaign,
    loading,
    error,
    refreshConfig,
    isFeatureEnabled,
    getEnvironment,
  }

  return (
    <ConfigContext.Provider value={value}>{children}</ConfigContext.Provider>
  )
}

export default ConfigContext
